import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Star, Quote } from "lucide-react";

const TESTIMONIALS = [
  {
    quote: "My daughter literally asks for 'her DreamWeaver story' every single night now. She's sleeping faster than ever because she's so at peace after each adventure.",
    name: "Sarah M.",
    role: "Parent of Luna, age 6",
    stars: 5,
    avatar: "S",
    color: "from-purple-600 to-violet-400",
  },
  {
    quote: "The watercolor illustrations genuinely blew me away. My son recognized himself as the hero immediately — he pointed and said 'that's me, Papa!' It was magical.",
    name: "James T.",
    role: "Parent of Theo, age 5",
    stars: 5,
    avatar: "J",
    color: "from-amber-600 to-orange-400",
  },
  {
    quote: "We've tried every bedtime app out there. Nothing comes close to DreamWeaver. The stories are calming, long enough, and always end at exactly the right moment.",
    name: "Priya K.",
    role: "Parent of Arjun, age 8",
    stars: 5,
    avatar: "P",
    color: "from-teal-500 to-cyan-400",
  },
  {
    quote: "My twins both wanted different stories — and DreamWeaver delivered perfectly personalized ones for each. Worth every cent of the subscription.",
    name: "Emily R.",
    role: "Parent of Mia & Max, age 4",
    stars: 5,
    avatar: "E",
    color: "from-rose-500 to-pink-400",
  },
  {
    quote: "The voice narration is stunning. We use 'Aria' every night and my daughter now sleeps with her eyes closed listening, which never happened before.",
    name: "David L.",
    role: "Parent of Sophie, age 7",
    stars: 5,
    avatar: "D",
    color: "from-indigo-600 to-blue-400",
  },
  {
    quote: "As a pediatric sleep consultant, I recommend DreamWeaver to all my clients. The structure — slowing down, gentle cues, peaceful endings — is textbook perfect.",
    name: "Dr. Anna C.",
    role: "Pediatric Sleep Consultant",
    stars: 5,
    avatar: "A",
    color: "from-green-600 to-emerald-400",
  },
];

function TestimonialCard({ t, delay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -5, transition: { duration: 0.25 } }}
      className="glass-card rounded-2xl p-6 flex flex-col gap-4 cursor-default border border-purple-500/10 hover:border-purple-500/25 transition-colors"
    >
      <div className="flex items-start gap-1">
        {Array.from({ length: t.stars }, (_, i) => (
          <Star key={i} size={12} className="text-amber-400 fill-amber-400" />
        ))}
      </div>

      <div className="relative">
        <Quote size={18} className="text-purple-400/30 absolute -top-1 -left-1" />
        <p className="text-sm text-slate-300 leading-relaxed font-serif italic pl-4">
          {t.quote}
        </p>
      </div>

      <div className="flex items-center gap-3 pt-2 border-t border-purple-500/10">
        <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${t.color} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
          {t.avatar}
        </div>
        <div>
          <p className="text-sm font-semibold text-white">{t.name}</p>
          <p className="text-xs text-slate-500">{t.role}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function TestimonialsSection() {
  return (
    <section data-testid="testimonials-section" className="py-24 lg:py-32 relative">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse 70% 40% at 50% 50%, rgba(245,158,11,0.04) 0%, transparent 60%)"
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="inline-block text-xs font-mono tracking-[0.2em] uppercase text-amber-300/80 mb-4"
          >
            From the Families
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
          >
            Bedtime transformed.<br />
            <span className="gradient-text">Every night.</span>
          </motion.h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={i} t={t} delay={i * 0.08} />
          ))}
        </div>

        {/* Summary stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8"
        >
          {[
            { value: "4.9/5", label: "Parent Rating", sub: "Based on 1,200+ reviews" },
            { value: "98%", label: "Better Sleep Reported", sub: "Within first week" },
            { value: "15 min", label: "Average Story Length", sub: "Perfect bedtime ritual" },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <p className="font-serif text-3xl font-bold gradient-text-gold">{stat.value}</p>
              <p className="font-semibold text-white mt-1 text-sm">{stat.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{stat.sub}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
